import { EndpointResponse } from '@/components/api/utils';
import { useQuery } from '@tanstack/react-query';

export interface Manager {
	id: string;
	name?: string | null;
	displayName?: string | null; // Added for better clarity
	email?: string | null;
	department?: string | null;
	managerId?: string | null; // ID of the manager
	manager?: Manager;
}

// Mock data for managers
// export const managers: Manager[] = [
// 	{
// 		id: 'mgr1',
// 		name: 'Sarah Johnson',
// 		email: 'sarah.johnson@company.com',
// 		department: 'Engineering',
// 	},
// 	{
// 		id: 'mgr2',
// 		name: 'Michael Chen',
// 		email: 'michael.chen@company.com',
// 		department: 'Marketing',
// 	},
// 	{
// 		id: 'mgr3',
// 		name: 'Emily Rodriguez',
// 		email: 'emily.rodriguez@company.com',
// 		department: 'Sales',
// 	},
// 	{
// 		id: 'mgr4',
// 		name: 'David Thompson',
// 		email: 'david.thompson@company.com',
// 		department: 'HR',
// 	},
// 	{
// 		id: 'mgr5',
// 		name: 'Lisa Wang',
// 		email: 'lisa.wang@company.com',
// 		department: 'Finance',
// 	},
// ];

// // Mock data for employees
// export const mockEmployees: Employee[] = [
// 	{
// 		id: 'emp1',
// 		name: 'John Smith',
// 		email: 'john.smith@company.com',
// 		department: 'Engineering',
// 		position: 'Senior Software Engineer',
// 		managerId: 'mgr1',
// 		managerName: 'Sarah Johnson',
// 	},
// 	{
// 		id: 'emp2',
// 		name: 'Anna Davis',
// 		email: 'anna.davis@company.com',
// 		department: 'Engineering',
// 		position: 'Frontend Developer',
// 		managerId: 'mgr1',
// 		managerName: 'Sarah Johnson',
// 	},
// 	{
// 		id: 'emp3',
// 		name: 'Robert Wilson',
// 		email: 'robert.wilson@company.com',
// 		department: 'Engineering',
// 		position: 'Backend Developer',
// 		managerId: 'mgr1',
// 		managerName: 'Sarah Johnson',
// 	},
// 	{
// 		id: 'emp4',
// 		name: 'Jennifer Brown',
// 		email: 'jennifer.brown@company.com',
// 		department: 'Marketing',
// 		position: 'Marketing Specialist',
// 		managerId: 'mgr2',
// 		managerName: 'Michael Chen',
// 	},
// 	{
// 		id: 'emp5',
// 		name: 'Mark Taylor',
// 		email: 'mark.taylor@company.com',
// 		department: 'Marketing',
// 		position: 'Content Manager',
// 		managerId: 'mgr2',
// 		managerName: 'Michael Chen',
// 	},
// 	{
// 		id: 'emp6',
// 		name: 'Jessica Martinez',
// 		email: 'jessica.martinez@company.com',
// 		department: 'Sales',
// 		position: 'Sales Representative',
// 		managerId: 'mgr3',
// 		managerName: 'Emily Rodriguez',
// 	},
// 	{
// 		id: 'emp7',
// 		name: 'Christopher Lee',
// 		email: 'christopher.lee@company.com',
// 		department: 'Sales',
// 		position: 'Account Manager',
// 		managerId: 'mgr3',
// 		managerName: 'Emily Rodriguez',
// 	},
// 	{
// 		id: 'emp8',
// 		name: 'Amanda White',
// 		email: 'amanda.white@company.com',
// 		department: 'HR',
// 		position: 'HR Coordinator',
// 		managerId: 'mgr4',
// 		managerName: 'David Thompson',
// 	},
// 	{
// 		id: 'emp9',
// 		name: 'Kevin Garcia',
// 		email: 'kevin.garcia@company.com',
// 		department: 'Finance',
// 		position: 'Financial Analyst',
// 		managerId: 'mgr5',
// 		managerName: 'Lisa Wang',
// 	},
// 	{
// 		id: 'emp10',
// 		name: 'Rachel Miller',
// 		email: 'rachel.miller@company.com',
// 		department: 'Finance',
// 		position: 'Accountant',
// 		managerId: 'mgr5',
// 		managerName: 'Lisa Wang',
// 	},
// ];

export const useManagersByEmailQuery = (email: string): Manager | undefined => {
	return useAllManagersQuery().find(
		(manager) => manager.email?.toLowerCase() === email.toLowerCase(),
	);
};

export const useEmployeesByManagerEmail = (managerEmail: string): Manager[] => {
	const manager = useManagersByEmailQuery(managerEmail);
	const allManagers = useAllManagersQuery();
	if (!manager) return [];

	return allManagers
		.filter((x) => x.managerId == manager.id)
		.map((x) => ({ ...x, manager: manager })) as Manager[];
};

export const useAllManagersQuery = (): Manager[] => {
	const { data } = useQuery({
		queryKey: ['employees'],
		queryFn: async () => {
			const rawResponse = await (
				await fetch('/api/get-employees')
			).json();
			const response = EndpointResponse.parse(rawResponse);
			return response.employees;
		},
		staleTime: 60 * 1000, // 1 minute
	});
	return data!.map((employee) => ({
		...employee,
	}));
};

// export const useAllEmployeesQuery = (): Employee[] => {
// 	return mockEmployees;
// };
